import {
  ArrowLeftOnRectangleIcon,
  ChartPieIcon,
  ChevronUpDownIcon,
  Cog8ToothIcon,
  SparklesIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline'
import { Menu } from '@headlessui/react'
import React, { createElement, useMemo, useContext } from 'react'
import { useQueryClient } from 'react-query'

import { UserCircleIcon } from '@heroicons/react/24/solid'
import {
  Form,
  useBasename,
  useCreatePath,
  useGetIdentity,
  useLogout,
  useResourceDefinitions,
} from 'ra-core'
import {
  Link,
  ReferenceInput,
  SelectInput,
  useDataProvider,
  usePermissions,
  useRefresh,
  useStore,
} from 'react-admin'
import { useMatch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from './common/Button'
import userPermissions from '../Users/userPermissions'
import { getLabelSuffix } from '../helpers'
import { DateRangeFilterContext } from '../contexts/DateRangeFilterContext'
import { ContractIcon } from '../Contracts/ContractList'

const UserMenu = () => {
  const { t, i18n } = useTranslation()
  const { permissions } = usePermissions()
  const { isLoading, data: identity } = useGetIdentity()
  const dp = useDataProvider()
  const logout = useLogout()
  const refresh = useRefresh()
  const queryClient = useQueryClient()
  const dateRangeFilter = useContext(DateRangeFilterContext)
  const [, setOrgSwitch] = useStore('organisation.switch', 0)

  if (isLoading || !identity) return null

  let user: any = localStorage.getItem('user')
  if (user) {
    user = JSON.parse(user)
  }

  const onChangeOrg = async (e) => {
    if (e.target.value) {
      const org = await dp.getOne('organisations', { id: e.target.value })
      console.log(org)
      await dp.update('users', {
        previousData: undefined,
        id: identity?.id,
        data: {
          organisation_id: Number(e.target.value),
          name: identity?.fullName || user?.name,
          email: identity?.email,
          permission_flags: user?.permission_flags,
        },
      })
      e.preventDefault()
      e.stopPropagation()
      const profile = await dp.getProfile()
      if (profile?.data) {
        user = {
          fullName: profile.data?.name || identity?.fullName,
          ...profile.data,
        }
        localStorage.setItem('user', JSON.stringify(user))
      }
      await dateRangeFilter.fetchUserDateRange(true)
      localStorage.removeItem('user')
      localStorage.setItem('defaultCurrency', org.data?.currency?.code || 'GBP')
      localStorage.setItem('defaultNoun', org.data?.noun || 'SPEND')
      queryClient.clear()
      setOrgSwitch(Date.now())
      refresh()
    }
  }

  return (
    <div className="text-sm space-y-2 relative">
      <Menu>
        <Menu.Button className="w-full p-3 border rounded-md active hover:bg-white">
          <div className="text-left flex items-center justify-between w-full space-x-2">
            <UserCircleIcon className="opacity-50 h-4" />
            <div className="font-medium flex-1">{identity?.fullName}</div>
            <ChevronUpDownIcon className="h-4" />
          </div>
        </Menu.Button>
        <Menu.Items className="bottom-12 bg-white rounded-md absolute left-0 shadow overflow-hidden border border-b-0 p-3 w-64 z-30">
          <Menu.Item>
            <div className="space-y-3">
              <div className="space-y-1">
                <div className="font-medium">{identity?.fullName}</div>
                <div className="text-xs opacity-70">{identity?.email}</div>
              </div>
              <div className="border-t border-b py-3 -mx-3 px-3">
                <Link
                  to="/admin/profile"
                  className="w-full flex items-center space-x-2 text-indigo-600 font-medium"
                >
                  <Cog8ToothIcon className="h-4" />
                  <span>{t('sidebar.manage')}</span>
                </Link>
              </div>
              <Button
                className="w-full justify-center"
                onClick={() => {
                  i18n.changeLanguage('en')
                  logout()
                }}
                icon={<ArrowLeftOnRectangleIcon className="h-5" />}
              >
                {t('logout')}
              </Button>
            </div>
          </Menu.Item>
        </Menu.Items>
      </Menu>

      {permissions & userPermissions.SUPER_ADMIN ? (
        <Form record={identity}>
          <ReferenceInput source="organisation_id" reference="organisations">
            <SelectInput
              className="w-full"
              label="Organisation"
              optionText="name"
              optionValue="id"
              onChange={onChangeOrg}
            />
          </ReferenceInput>
        </Form>
      ) : null}
    </div>
  )
}

const NavigationItem = ({ icon, label, url }) => {
  const match = useMatch(url)
  return (
    <Link
      to={url}
      key={label}
      className={`text-sm hover:bg-stone-200/70 active flex items-center space-x-3 px-4 py-3 text-black rounded-lg min-w-[180px] border ${
        match
          ? '!font-medium bg-white/80 border-stone-300/70'
          : 'border-transparent'
      }`}
    >
      {icon}
      <span className="text-black pr-2">{label}</span>
    </Link>
  )
}

const Navigation = () => {
  const resources = useResourceDefinitions()
  const createPath = useCreatePath()
  const { t } = useTranslation()
  const basename = useBasename()
  const suffix = getLabelSuffix()
  const { data: identity } = useGetIdentity()

  // Check if show_risk is enabled for the organization and user can administer vendors
  const { permissions } = usePermissions()
  const showRisk = React.useMemo(() => {
    // Show Risk menu if show_risk is enabled for the organization AND user has VIEW_TRANSACTIONS permission
    return (
      (identity?.organisation?.show_risk &&
        permissions & userPermissions.VIEW_TRANSACTIONS) ||
      false
    )
  }, [identity, permissions])

  const showContracts = React.useMemo(() => {
    // Show Contracts menu if show_contracts is enabled for the organization AND user has VIEW_TRANSACTIONS permission
    return (
      (identity?.organisation?.show_contracts &&
        permissions & userPermissions.VIEW_TRANSACTIONS) ||
      false
    )
  }, [identity, permissions])

  const showML = React.useMemo(() => {
    // Show ML menu if show_ml is enabled for the organization AND user has VIEW_TRANSACTIONS permission
    return (
      (identity?.organisation?.show_ml &&
        permissions & userPermissions.VIEW_TRANSACTIONS) ||
      false
    )
  }, [identity, permissions])

  const items = useMemo(() => {
    const getLabel = (name) => {
      if (name === 'transactions' && suffix === '_alt') return t(`claims`)
      if (name === 'vendors' && suffix === '_alt') return t(`suppliers`)
      return t(`${name}`)
    }

    const baseItems = [
      {
        icon: <ChartPieIcon className="h-5 text-black" />,
        label: t('dashboard'),
        url: basename,
      },
      ...Object.keys(resources)
        .filter(
          (name) =>
            resources[name].hasList &&
            name !== 'tags' &&
            name !== 'contracts' &&
            name !== 'contract-ai-prompts' &&
            name !== 'contract-categorisation-options' &&
            name !== 'contract-extraction-settings' &&
            name !== 'ml-tasks'
        )
        .map((name) => ({
          icon: resources[name].icon ? (
            createElement(resources[name].icon, { className: 'h-5 text-black' })
          ) : (
            <>-</>
          ),
          url: createPath({
            resource: name,
            type: 'list',
          }),
          label: getLabel(name),
        })),
    ]

    // Add Risk tab if show_risk is enabled
    const finalItems = [...baseItems]

    if (showRisk) {
      // Find the index of the Vendors menu item by looking for a URL that contains 'vendors'
      const vendorsIndex = finalItems.findIndex(
        (item) =>
          item.url.includes('/vendors') ||
          (item.label &&
            (item.label.toLowerCase() === t('vendors').toLowerCase() ||
              item.label.toLowerCase() === t(`vendors${suffix}`).toLowerCase()))
      )

      // If Vendors is found, insert Risk right after it, otherwise add it after Dashboard
      const insertIndex = vendorsIndex !== -1 ? vendorsIndex + 1 : 1

      finalItems.splice(insertIndex, 0, {
        icon: <ShieldExclamationIcon className="h-5 text-black" />,
        label: t('Risk'),
        url: `${basename}/risk`,
      })
    }

    if (showContracts) {
      // Find the index of the Risk menu item (if it exists) or Vendors
      const riskIndex = finalItems.findIndex(
        (item) => item.url === `${basename}/risk`
      )
      const vendorsIndex = finalItems.findIndex((item) =>
        item.url.includes('/vendors')
      )

      // Insert after Risk, or after Vendors, or after Dashboard
      const insertIndex =
        riskIndex !== -1
          ? riskIndex + 1
          : vendorsIndex !== -1
          ? vendorsIndex + 1
          : 1

      finalItems.splice(insertIndex, 0, {
        icon: <ContractIcon className="h-5 text-black" />,
        label: t('contracts'),
        url: `${basename}/contracts`,
      })

      finalItems.splice(insertIndex + 1, 0, {
        icon: <SparklesIcon className="h-5 text-black" />,
        label: t('contract-ai-prompts'),
        url: `${basename}/contract-ai-prompts`,
      })

      finalItems.splice(insertIndex + 2, 0, {
        icon: <SparklesIcon className="h-5 text-black" />,
        label: t('contract-categorisation-options'),
        url: `${basename}/contract-categorisation-options`,
      })

      finalItems.splice(insertIndex + 3, 0, {
        icon: <SparklesIcon className="h-5 text-black" />,
        label: t('contract-extraction-settings'),
        url: `${basename}/contract-extraction-settings`,
      })
    }

    if (showML) {
      // Find the index of Contracts to insert after it
      const contractsIndex = finalItems.findIndex(
        (item) => item.url === `${basename}/contracts`
      )
      const insertIndex =
        contractsIndex !== -1 ? contractsIndex + 1 : finalItems.length

      finalItems.splice(insertIndex, 0, {
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-5 text-black"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 3.75H6.912a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H15M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859M12 3v8.25m0 0l-3-3m3 3l3-3"
            />
          </svg>
        ),
        label: t('ml-tasks'),
        url: `${basename}/ml-tasks`,
      })
    }

    return finalItems
  }, [
    basename,
    createPath,
    resources,
    suffix,
    t,
    showRisk,
    showContracts,
    showML,
  ])

  return (
    <nav className="space-y-1 -ml-4">
      {items.map((item) => (
        <NavigationItem
          url={item.url}
          icon={item.icon}
          label={item.label}
          key={`${item.label}-${item.url}`}
        />
      ))}
    </nav>
  )
}

export const Sidebar = () => (
  <aside className="min-h-screen flex flex-col justify-between sticky top-0 py-12 pr-8 border-r border-stone-200/60 w-[230px] shrink-0">
    <div className="space-y-16">
      <a className="flex items-center space-x-3 active" href="/admin">
        <img src="/logo.png" className="h-7" alt="SpendQube logo" />
        <h1>
          Spend<span className="font-medium">Qube</span>
        </h1>
      </a>
      <Navigation />
    </div>
    <UserMenu />
  </aside>
)
